import { inject, Injectable, signal, WritableSignal } from '@angular/core';
import {
  AbstractControl,
  FormArray,
  FormBuilder,
  FormControl,
  FormGroup,
  ValidatorFn,
  Validators,
} from '@angular/forms';
import { startWith } from 'rxjs/operators';
import { CustomValidators } from '../../form';
import {
  DynamicFormDTO,
  FieldTypeENUM,
  IDynamicForm,
  IFields,
  ISection,
} from '../models';
import { ScaleDefinition } from '../models/dynamic-scale.model';

/**
 * Saved values of a scale field, keyed by the scale's field/computed-field key.
 */
type ScaleSavedValues = Partial<Record<string, number | string>>;

/**
 * The raw value of an option FormGroup built by `addOptionsAndConfigControls`.
 */
interface OptionControlValue {
  id?: string | number;
  name?: string;
  selected?: boolean;
}

@Injectable({ providedIn: 'root' })
export class DynamicFormViewerService {
  /**
   * The formbuilder.
   */
  private readonly fb = inject(FormBuilder);

  /**
   * A signal holding the current formgroup.
   */
  private readonly _formGroup: WritableSignal<FormGroup | null> = signal(null);

  /**
   * Publicly exposed readonly signal of the form group.
   */
  readonly formGroup = this._formGroup.asReadonly();

  /**
   * Constructs the main FormGroup for the viewer based on dynamic data.
   * @param {IDynamicForm} data The parsed JSON configuration.
   * @returns {FormGroup} The constructed reactive form.
   */
  buildViewerForm(data: IDynamicForm): FormGroup {
    const form = this.createViewerForm(data);
    this._formGroup.set(form);
    return form;
  }

  /**
   * Builds each given form from its JSON schema and checks its validity, skipping the absent ones.
   * @returns {boolean} True if every present form is valid
   */
  areFormsValid(...forms: (DynamicFormDTO | null | undefined)[]): boolean {
    return forms
      .filter((dto): dto is DynamicFormDTO => !!dto?.dynamicJson)
      .every(dto => this.isFormValid(dto.dynamicJson));
  }

  /**
   * Builds a standalone form from the JSON schema, with its conditional logic, and checks its validity.
   * @returns {boolean} True if the form is valid, false if invalid or the JSON is malformed
   */
  private isFormValid(dynamicJson: string): boolean {
    try {
      const form = this.createViewerForm(
        JSON.parse(dynamicJson) as IDynamicForm
      );
      this.setupConditionalLogic(form);
      return form.valid;
    } catch {
      return false;
    }
  }

  /**
   * Creates the main FormGroup from the dynamic data, without storing it as the current form.
   * @returns {FormGroup} The constructed reactive form
   */
  private createViewerForm(data: IDynamicForm): FormGroup {
    const sectionsArray = (data.sections || []).map(section =>
      this.buildViewerSection(section)
    );

    return this.fb.group({
      id: [data.id],
      title: [data.title],
      description: [data.description],
      sections: this.fb.array(sectionsArray),
    });
  }

  /**
   * Clears the current form state.
   */
  clearForm(): void {
    this._formGroup.set(null);
  }

  /**
   * Constructs a FormGroup for a specific section.
   * @param {ISection} section The section data.
   * @returns {FormGroup} The section form group.
   */
  private buildViewerSection(section: ISection): FormGroup {
    const fieldsArray = (section.fields || []).map(field =>
      this.buildViewerField(field)
    );

    return this.fb.group({
      id: [section.id],
      title: [section.title],
      description: [section.description],
      fields: this.fb.array(fieldsArray),
    });
  }

  /**
   * Constructs a FormGroup for an individual field, orchestrating its controls.
   * @param {IFields} field The field configuration.
   * @returns {FormGroup} The field form group.
   */
  private buildViewerField(field: IFields): FormGroup {
    const group = this.createBaseFieldGroup(field);

    this.addStandardControls(group, field);
    this.addConditionalControls(group, field);
    this.addValueControl(group, field);
    this.addOptionsAndConfigControls(group, field);
    this.addAccordionFieldsControl(group, field);

    return group;
  }

  /**
   * Creates the base FormGroup with generic field properties.
   */
  private createBaseFieldGroup(field: IFields): FormGroup {
    const placeholderValue = this.getPlaceholderValue(field);
    const labelValue = this.getPredefinedLabel(field);

    return this.fb.group({
      id: [field.id],
      type: [field.type],
      label: [labelValue],
      placeholder: [placeholderValue],
    });
  }

  /**
   * Adds standard controls like required status, endpoints, and ranges.
   */
  private addStandardControls(group: FormGroup, field: IFields): void {
    const isToggle = field.type === FieldTypeENUM.TOGGLE;
    const isTypology = field.type === FieldTypeENUM.TYPOLOGY;
    const hasRange = [
      FieldTypeENUM.NUMBER,
      FieldTypeENUM.TEXT,
      FieldTypeENUM.TEXTAREA,
    ].includes(field.type as FieldTypeENUM);

    if (field.required !== undefined && !isToggle) {
      group.addControl('required', this.fb.control(field.required));
    }

    if (isTypology && field.endpoint !== undefined) {
      group.addControl('endpoint', this.fb.control(field.endpoint));
    }

    if (hasRange) {
      if (field.min !== undefined)
        group.addControl('min', this.fb.control(field.min));
      if (field.max !== undefined)
        group.addControl('max', this.fb.control(field.max));
    }
  }

  /**
   * Adds conditional logic properties to the group if they exist.
   */
  private addConditionalControls(group: FormGroup, field: IFields): void {
    const conditionProp = [
      'hasCondition',
      'conditionFieldId',
      'conditionValue',
    ];

    conditionProp.forEach(prop => {
      if (field && prop in field) {
        group.addControl(prop, this.fb.control(field[prop]));
      }
    });
  }

  /**
   * Determines and attaches the correct 'value' control based on the field type.
   */
  private addValueControl(group: FormGroup, field: IFields): void {
    const isScale = this.isScaleField(field.type);
    const isGeo = field.type === FieldTypeENUM.REGIONI_PROVINCE_COMUNI;
    const isToggle = field.type === FieldTypeENUM.TOGGLE;
    const isAccordion = field.type === FieldTypeENUM.ACCORDION;

    if (isAccordion) {
      return;
    } else if (isScale) {
      group.addControl(
        'value',
        this.buildScaleFormGroupFromConfig(
          field.config!,
          this.getSavedScaleValues(field.value)
        )
      );
    } else if (isGeo) {
      group.addControl('value', this.buildGeoFormGroup(field.required));
    } else {
      const defaultValue = isToggle
        ? (field.value ?? false)
        : (field.value ?? null);
      group.addControl(
        'value',
        this.fb.control(defaultValue, this.getFieldValidators(field))
      );
    }
  }

  /**
   * Adds the options FormArray (for selects, checkboxes, etc.) and specific configs.
   */
  private addOptionsAndConfigControls(group: FormGroup, field: IFields): void {
    if (field.config) {
      group.addControl('config', this.fb.control(field.config));
    }

    if (field.options && field.options.length > 0) {
      const optionsArray = field.options.map((opt, index) =>
        this.fb.group({
          id: [opt.id],
          name: [opt.name || `Opzione ${index + 1}`],
          selected: [opt.selected ?? false],
        })
      );

      group.addControl('options', this.fb.array(optionsArray));

      if (field.type === FieldTypeENUM.CHECKBOX) {
        this.setupCheckboxListener(group);
      }
    }
  }

  /**
   * Adds nested reactive controls for accordion fields.
   */
  private addAccordionFieldsControl(group: FormGroup, field: IFields): void {
    if (field.type !== FieldTypeENUM.ACCORDION) return;

    const fieldsArray = (field.fields || []).map(child =>
      this.buildViewerField(child)
    );
    group.addControl('fields', this.fb.array(fieldsArray));
  }

  /**
   * Computes the placeholder based on field type and constraints.
   */
  private getPlaceholderValue(field: IFields): string {
    if (field.type === FieldTypeENUM.NUMBER) {
      return `Inserisci un valore compreso tra ${field.min ?? 0} e ${field.max ?? 100}`;
    }
    return field.placeholder || '';
  }

  /**
   * Returns a predefined label for specific field types, or the standard label.
   */
  private getPredefinedLabel(field: IFields): string {
    if (field.type === FieldTypeENUM.PRIORITY) return 'PRIORITÀ';
    if (this.isScaleField(field.type)) {
      return field.config?.title || field.label || field.type.replace('_', ' ');
    }
    return field.label || '';
  }

  /**
   * Constructs the internal FormGroup structure for Geo fields.
   */
  private buildGeoFormGroup(isRequired = false): FormGroup {
    const groupValidators: ValidatorFn[] = [CustomValidators.geoGroupValidator];
    if (isRequired) {
      groupValidators.push(CustomValidators.geoGroupRequired);
    }

    return this.fb.group(
      {
        regione: [null, [CustomValidators.geoRequiredIfSiblingsSet]],
        provincia: [null, [CustomValidators.geoRequiredIfSiblingsSet]],
        comune: [null, [CustomValidators.geoRequiredIfSiblingsSet]],
      },
      { validators: groupValidators }
    );
  }

  /**
   * Constructs the internal FormGroup structure for medical scale fields,
   * restoring the previously saved values when present.
   */
  private buildScaleFormGroupFromConfig(
    config: ScaleDefinition,
    savedValues: ScaleSavedValues = {}
  ): FormGroup {
    const controls: Record<string, unknown[]> = {};

    if (config?.sections) {
      config.sections.forEach(section => {
        section.fields.forEach(f => {
          controls[f.key] = [
            savedValues[f.key] ?? (f.min || 0),
            [Validators.required, Validators.min(f.min), Validators.max(f.max)],
          ];
        });
      });
    }

    if (config?.computedFields) {
      config.computedFields.forEach(cf => {
        controls[cf.key] = [savedValues[cf.key] ?? 0];
      });
    }

    return this.fb.group(controls);
  }

  /**
   * Extracts the saved scale values from a field value, ignoring non-object values.
   * @returns {ScaleSavedValues} the saved values keyed by scale field key
   */
  private getSavedScaleValues(value: IFields['value']): ScaleSavedValues {
    const isObject =
      value !== null && typeof value === 'object' && !(value instanceof Date);
    return isObject ? (value as unknown as ScaleSavedValues) : {};
  }

  // --------------------------------------------------------------------------
  // VALIDATION LOGIC
  // --------------------------------------------------------------------------

  /**
   * Modifies validators dynamically. If hidden, clears validators and values.
   * If visible, restores the original validators.
   */
  updateFieldValidators(fieldGroup: FormGroup, isVisible: boolean): void {
    const valueControl = fieldGroup.get('value');
    if (!valueControl) return;

    if (!isVisible) {
      valueControl.clearValidators();
      valueControl.setValue(null, { emitEvent: false });
    } else {
      const fieldData = fieldGroup.getRawValue() as IFields;
      valueControl.setValidators(this.getFieldValidators(fieldData));
    }
    valueControl.updateValueAndValidity({ emitEvent: false });
  }

  /**
   * Retrieves the validators for a field based on its specific type and configuration.
   */
  private getFieldValidators(field: IFields): ValidatorFn[] {
    const validators: ValidatorFn[] = [];
    if (field.required) validators.push(Validators.required);

    switch (field.type) {
      case FieldTypeENUM.NUMBER:
        if (field.min !== undefined && field.min !== null) {
          validators.push(Validators.min(field.min));
        }
        if (field.max !== undefined && field.max !== null) {
          validators.push(Validators.max(field.max));
        }
        break;

      case FieldTypeENUM.TEXT:
      case FieldTypeENUM.TEXTAREA:
        if (field.min !== undefined && field.min !== null) {
          validators.push(Validators.minLength(field.min));
        }
        if (field.max !== undefined && field.max !== null) {
          validators.push(Validators.maxLength(field.max));
        }
        break;

      case FieldTypeENUM.EMAIL:
        validators.push(Validators.email);
        break;

      case FieldTypeENUM.TELEFONO:
        validators.push(CustomValidators.invalidPhoneNumber);
        break;

      case FieldTypeENUM.CODICE_FISCALE:
        validators.push(CustomValidators.invalidCf);
        break;
    }

    return validators;
  }

  /**
   * Scans the form to find conditioned fields and establishes reactive listeners on their parent fields.
   * @param {FormGroup} form The completely built main form.
   */
  setupConditionalLogic(form: FormGroup): void {
    const allFields = new Map<string, FormGroup>();
    const sections = form.get('sections') as FormArray;

    if (!sections) return;

    sections.controls.forEach(section => {
      const fields = section.get('fields') as FormArray;
      this.collectFields(fields, allFields);
    });

    allFields.forEach(fieldGroup => {
      const hasCond = fieldGroup.get('hasCondition')?.value;
      const condFieldId = fieldGroup.get('conditionFieldId')?.value;
      const condValue = fieldGroup.get('conditionValue')?.value;

      if (hasCond && condFieldId && allFields.has(condFieldId)) {
        const parentGroup = allFields.get(condFieldId)!;
        const parentType = parentGroup.get('type')?.value;

        if (parentType === FieldTypeENUM.CHECKBOX) {
          const optionsArray = parentGroup.get('options') as FormArray;
          if (optionsArray) {
            optionsArray.valueChanges
              .pipe(startWith(optionsArray.value))
              .subscribe(() => {
                const isMet = this.compareCondition(parentGroup, condValue);
                this.toggleFieldValidators(fieldGroup, isMet);
              });
          }
        } else {
          const parentValueControl = parentGroup.get('value');
          if (parentValueControl) {
            parentValueControl.valueChanges
              .pipe(startWith(parentValueControl.value))
              .subscribe(() => {
                const isMet = this.compareCondition(parentGroup, condValue);
                this.toggleFieldValidators(fieldGroup, isMet);
              });
          }
        }
      }
    });
  }

  /**
   * Collects fields recursively, including accordion children.
   */
  private collectFields(
    fields: FormArray | null,
    allFields: Map<string, FormGroup>
  ): void {
    if (!fields) return;

    fields.controls.forEach(field => {
      const fieldGroup = field as FormGroup;
      allFields.set(fieldGroup.get('id')?.value, fieldGroup);

      if (fieldGroup.get('type')?.value === FieldTypeENUM.ACCORDION) {
        this.collectFields(fieldGroup.get('fields') as FormArray, allFields);
      }
    });
  }

  /**
   * Safely evaluates if the condition is met based on the parent's current state.
   */
  compareCondition(
    parentGroup: FormGroup | null,
    targetValue: unknown
  ): boolean {
    if (
      !parentGroup ||
      typeof parentGroup.get !== 'function' ||
      targetValue === null ||
      targetValue === undefined
    ) {
      return false;
    }

    const parentType = parentGroup.get('type')?.value;

    if (parentType === FieldTypeENUM.CHECKBOX) {
      const optionsArray = parentGroup.get('options') as FormArray;
      if (!optionsArray) return false;

      const optionsValue: OptionControlValue[] = optionsArray.getRawValue();
      const targetOption = optionsValue.find(
        opt => String(opt.id) === String(targetValue)
      );
      return targetOption ? targetOption.selected === true : false;
    }

    const currentValue = parentGroup.get('value')?.value;
    if (currentValue === null || currentValue === undefined) return false;

    const extractedTarget =
      typeof targetValue === 'object' &&
      targetValue !== null &&
      'id' in targetValue
        ? targetValue.id
        : targetValue;
    const strTarget = String(extractedTarget).trim().toLowerCase();

    if (typeof currentValue === 'object' && currentValue !== null) {
      const currentIdStr =
        currentValue.id !== undefined
          ? String(currentValue.id).trim().toLowerCase()
          : null;
      if (currentIdStr === strTarget) return true;

      const textFields = [
        currentValue.name,
        currentValue.nome,
        currentValue.descrizione,
        currentValue.label,
      ];

      return textFields.some(
        text =>
          text !== undefined &&
          text !== null &&
          String(text).trim().toLowerCase() === strTarget
      );
    }

    const strCurrent = String(currentValue).trim().toLowerCase();
    const priorityMap: Record<string, string> = {
      alta: '1',
      media: '2',
      bassa: '3',
    };

    const finalCurrent =
      parentType === FieldTypeENUM.PRIORITY
        ? (priorityMap[strCurrent] ?? strCurrent)
        : strCurrent;

    return finalCurrent === strTarget;
  }

  /**
   * Applies the result of a condition check by adding/removing validators and resetting values.
   */
  private toggleFieldValidators(
    fieldGroup: FormGroup,
    isVisible: boolean
  ): void {
    const valueControl = fieldGroup.get('value');
    if (!valueControl) {
      const fieldsControl = fieldGroup.get('fields');
      if (!fieldsControl) return;

      if (!isVisible) {
        fieldsControl.disable({ emitEvent: false });
      } else {
        fieldsControl.enable({ emitEvent: false });
      }
      return;
    }

    if (!isVisible) {
      this.clearControlData(valueControl);
    } else {
      this.restoreControlValidators(fieldGroup, valueControl);
    }

    valueControl.updateValueAndValidity({ emitEvent: true });
  }

  /**
   * Clears data and validators for fields that should be hidden.
   */
  private clearControlData(valueControl: AbstractControl): void {
    if (valueControl instanceof FormControl) {
      valueControl.clearValidators();
      valueControl.setValue(null, { emitEvent: false });
    } else if (valueControl instanceof FormGroup) {
      valueControl.clearValidators();
      valueControl.reset(undefined, { emitEvent: false });
    }
  }

  /**
   * Restores validators for fields that become visible.
   */
  private restoreControlValidators(
    fieldGroup: FormGroup,
    valueControl: AbstractControl
  ): void {
    const fieldData = fieldGroup.getRawValue() as IFields;
    valueControl.setValidators(this.getFieldValidators(fieldData));

    // Special handling for nested complex objects like Geo regions
    if (
      fieldData.type === FieldTypeENUM.REGIONI_PROVINCE_COMUNI &&
      fieldData.required
    ) {
      valueControl.setValidators([
        CustomValidators.geoGroupValidator,
        CustomValidators.geoGroupRequired,
      ]);
    }
  }

  /**
   * Subscribes to checkbox FormArray changes to concat names into a single string value.
   */
  private setupCheckboxListener(fieldGroup: FormGroup): void {
    const options = fieldGroup.get('options') as FormArray;

    options.valueChanges.subscribe(() => {
      const selectedNames = (options.getRawValue() as OptionControlValue[])
        .filter(opt => opt.selected)
        .map(opt => opt.name);

      fieldGroup
        .get('value')
        ?.setValue(selectedNames.join(', '), { emitEvent: false });
    });
  }

  // --------------------------------------------------------------------------
  // UTILITIES
  // --------------------------------------------------------------------------

  /**
   * Checks if a specific string corresponds to a medical scale type.
   * @param {string} type The field type to check.
   * @returns {boolean} True if the type is a scale.
   */
  isScaleField(type: string): boolean {
    const scales = [
      FieldTypeENUM.BARTHEL_SCALE,
      FieldTypeENUM.BRADEN_SCALE,
      FieldTypeENUM.MINIMENTAL_SCALE,
      FieldTypeENUM.NRS_SCALE,
      FieldTypeENUM.VAS_SCALE,
      FieldTypeENUM.MAS,
      FieldTypeENUM.NDI,
      FieldTypeENUM.RMDQ,
      FieldTypeENUM.SPPB,
      FieldTypeENUM.NINE_HOLE_PEG_TEST_SCALE,
      FieldTypeENUM.CAT,
      FieldTypeENUM.DASH,
      FieldTypeENUM.IPAQ,
      FieldTypeENUM.TCT,
      FieldTypeENUM.MOTRICITY,
    ];
    return scales.includes(type as FieldTypeENUM);
  }
}
